import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '../../hooks/useScrollAnimation';

export default function HomePage() {
  const [email, setEmail] = useState('');
  const [currentProductSlide, setCurrentProductSlide] = useState(0);
  const [hoveredProduct, setHoveredProduct] = useState<number | null>(null);

  // Scroll animation refs
  const metricsAnim = useScrollAnimation();
  const productsAnim = useScrollAnimation();
  const eduAnim = useStaggerAnimation(3);
  const blogAnim = useScrollAnimation();
  const newsletterAnim = useScrollAnimation();
  const communityAnim = useScrollAnimation();

  const featuredProducts = [
    {
      id: 1,
      name: 'Premium CBD Oil 1000mg',
      category: 'CBD Products',
      price: 49.99,
      originalPrice: 69.99,
      image: 'https://public.readdy.ai/ai/img_res/ba29f5a8a30ad293dca86bede32bce57.jpg'
    },
    {
      id: 2,
      name: 'Organic Hemp Seeds - Premium Strain',
      category: 'Seeds & Gear',
      price: 89.99,
      originalPrice: 119.99,
      image: 'https://readdy.ai/api/search-image?query=premium%20cannabis%20hemp%20seeds%20in%20elegant%20glass%20jar%20on%20natural%20wood%20surface%20with%20soft%20green%20leaves%20around%20clean%20white%20background%20professional%20product%20photography%20organic%20natural%20aesthetic%20high%20end%20wellness%20branding&width=400&height=500&seq=seed001&orientation=portrait'
    },
    {
      id: 3,
      name: 'Natural Organic Fertilizer Kit',
      category: 'Growing Supplies',
      price: 34.99,
      originalPrice: 49.99,
      image: 'https://readdy.ai/api/search-image?query=organic%20fertilizer%20bottles%20and%20natural%20soil%20amendments%20on%20clean%20white%20surface%20with%20green%20plant%20sprouts%20professional%20product%20photography%20eco%20friendly%20packaging%20minimalist%20natural%20aesthetic%20wellness%20gardening%20products&width=400&height=500&seq=fert001&orientation=portrait'
    },
    {
      id: 4,
      name: 'CBD Wellness Capsules 30ct',
      category: 'CBD Products',
      price: 39.99,
      originalPrice: 54.99,
      image: 'https://readdy.ai/api/search-image?query=elegant%20white%20bottle%20of%20CBD%20wellness%20capsules%20on%20marble%20surface%20with%20fresh%20green%20cannabis%20leaves%20clean%20white%20background%20professional%20product%20photography%20premium%20supplement%20packaging%20natural%20health%20aesthetic&width=400&height=500&seq=caps001&orientation=portrait'
    },
    {
      id: 5,
      name: 'Full Spectrum CBD Tincture',
      category: 'CBD Products',
      price: 59.99,
      originalPrice: 79.99,
      image: 'https://readdy.ai/api/search-image?query=premium%20dark%20amber%20CBD%20tincture%20bottle%20with%20gold%20label%20on%20white%20marble%20with%20green%20hemp%20leaves%20professional%20product%20photography%20luxury%20wellness%20product%20clean%20minimalist%20composition%20natural%20lighting&width=400&height=500&seq=tinc001&orientation=portrait'
    }
  ];

  const blogPosts = [
    {
      id: 1,
      title: 'Complete Guide to Organic Pest Management',
      category: 'Growing Tips',
      excerpt: 'Learn natural methods to protect your cannabis plants from common pests without harmful chemicals.',
      author: 'H Boss',
      date: 'Jan 15, 2025',
      readTime: '8 min read',
      image: 'https://readdy.ai/api/search-image?query=healthy%20green%20cannabis%20plant%20leaves%20with%20magnifying%20glass%20examining%20for%20pests%20in%20bright%20greenhouse%20natural%20sunlight%20organic%20farming%20educational%20content%20clean%20professional%20photography%20sustainable%20agriculture&width=800&height=450&seq=blog001&orientation=landscape'
    },
    {
      id: 2,
      title: 'Sustainable Growing: Worm Composting Basics',
      category: 'Sustainability',
      excerpt: 'Discover how worm cultivation creates the perfect organic fertilizer for thriving cannabis plants.',
      author: 'HR Boss',
      date: 'Jan 12, 2025',
      readTime: '6 min read',
      image: 'https://readdy.ai/api/search-image?query=rich%20dark%20compost%20soil%20with%20earthworms%20in%20wooden%20composting%20bin%20surrounded%20by%20green%20plants%20natural%20outdoor%20setting%20organic%20farming%20sustainable%20agriculture%20educational%20photography%20earthy%20natural%20tones&width=800&height=450&seq=blog002&orientation=landscape'
    },
    {
      id: 3,
      title: 'Beginner\'s Guide to Cannabis Cultivation',
      category: 'Beginner Guides',
      excerpt: 'Everything you need to know to start your first cannabis grow successfully from seed to harvest.',
      author: 'A Boss',
      date: 'Jan 10, 2025',
      readTime: '10 min read',
      image: 'https://readdy.ai/api/search-image?query=young%20cannabis%20seedlings%20sprouting%20in%20organic%20soil%20pots%20on%20wooden%20table%20with%20gardening%20tools%20bright%20natural%20light%20beginner%20friendly%20setup%20clean%20educational%20photography%20sustainable%20growing%20aesthetic&width=800&height=450&seq=blog003&orientation=landscape'
    }
  ];

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setEmail('');
  };

  const scrollProducts = (direction: 'left' | 'right') => {
    const isMobile = window.innerWidth < 768;
    const maxSlide = isMobile ? featuredProducts.length - 1 : featuredProducts.length - 3;
    
    if (direction === 'right' && currentProductSlide < maxSlide) {
      setCurrentProductSlide(currentProductSlide + 1);
    } else if (direction === 'left' && currentProductSlide > 0) {
      setCurrentProductSlide(currentProductSlide - 1);
    }
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative h-screen w-full flex items-center justify-center overflow-hidden bg-black">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/175fc0d3a7deb119f46e9d68342e64a7/92d27348b4c19597435c82fde837cead.png"
            alt="Elixir Sue Hero"
            className="w-full h-full object-contain object-center animate-[kenburns_20s_ease-in-out_infinite_alternate]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40"></div>
        </div>

        {/* Smoke Stream Effect */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* Stream 1 - left side */}
          <div className="smoke-stream smoke-stream-1"></div>
          {/* Stream 2 - center-left */}
          <div className="smoke-stream smoke-stream-2"></div>
          {/* Stream 3 - center */}
          <div className="smoke-stream smoke-stream-3"></div>
          {/* Stream 4 - center-right */}
          <div className="smoke-stream smoke-stream-4"></div>
          {/* Stream 5 - right side */}
          <div className="smoke-stream smoke-stream-5"></div>

          {/* Wispy puffs */}
          <div className="smoke-puff smoke-puff-1"></div>
          <div className="smoke-puff smoke-puff-2"></div>
          <div className="smoke-puff smoke-puff-3"></div>
          <div className="smoke-puff smoke-puff-4"></div>
          <div className="smoke-puff smoke-puff-5"></div>
          <div className="smoke-puff smoke-puff-6"></div>
        </div>

        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto w-full animate-[fadeInUp_1s_ease-out_0.3s_both]">
          <div className="inline-block px-5 py-2 border border-[#D4AF37]/60 rounded-full text-[#D4AF37] text-sm sm:text-sm font-semibold tracking-widest uppercase mb-4 sm:mb-6 animate-[fadeInDown_0.8s_ease-out_both]">
            Natural · Organic · Global
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 tracking-wide leading-tight px-2">
            YOUR TRUSTED GLOBAL CANNABIS<br className="hidden sm:block" />
            <span className="sm:hidden"> </span>WELLNESS EXPERTS
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-[#F5F1E8] font-light mb-4 sm:mb-8">
            Natural Healing Worldwide
          </p>
          <p className="text-sm sm:text-base md:text-lg text-white/90 mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed px-4">
            Premium organic products, expert growing tips, and sustainable practices for all
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 px-4">
            <Link
              to="/shop"
              className="w-full sm:w-auto group bg-[#2D5F3F] text-white px-8 sm:px-10 py-3 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#D4AF37] hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-2xl"
            >
              SHOP CBD PRODUCTS
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
            </Link>
            <a
              href="#community"
              className="w-full sm:w-auto group border-2 border-white text-white px-8 sm:px-10 py-3 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-white hover:text-[#2D5F3F] hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-global-line text-xl group-hover:rotate-12 transition-transform duration-300"></i>
              JOIN GLOBAL COMMUNITY
            </a>
          </div>
        </div>

        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-4xl text-white opacity-80"></i>
        </div>
      </section>

      {/* Trust Metrics Bar */}
      <section
        ref={metricsAnim.ref as React.RefObject<HTMLElement>}
        className="w-full bg-[#D4AF37]/15 py-6 sm:py-8"
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {[
              { icon: 'ri-leaf-line', number: '50K+', label: 'Customers Worldwide' },
              { icon: 'ri-global-line', number: '25+', label: 'Years Expertise' },
              { icon: 'ri-plant-line', number: '100%', label: 'Organic Methods' },
              { icon: 'ri-shield-check-line', number: 'Global', label: 'Shipping Available' }
            ].map((metric, index) => (
              <div
                key={index}
                className="text-center transition-all duration-700"
                style={{
                  opacity: metricsAnim.isVisible ? 1 : 0,
                  transform: metricsAnim.isVisible ? 'translateY(0)' : 'translateY(30px)',
                  transitionDelay: `${index * 120}ms`
                }}
              >
                <i className={`${metric.icon} text-3xl sm:text-4xl text-[#D4AF37] mb-2 sm:mb-3 block`}></i>
                <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1A3A2A] mb-1 sm:mb-2">{metric.number}</div>
                <div className="text-xs sm:text-sm uppercase tracking-wide text-gray-600 px-2">{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Carousel */}
      <section
        ref={productsAnim.ref as React.RefObject<HTMLElement>}
        className="py-16 sm:py-24 md:py-32 px-4"
        style={{
          opacity: productsAnim.isVisible ? 1 : 0,
          transform: productsAnim.isVisible ? 'translateY(0)' : 'translateY(50px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 sm:mb-12 md:mb-16 gap-4">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#2C2C2C]">
              Premium Wellness<br />Collection
            </h2>
            <div className="flex gap-3 sm:gap-4">
              <button
                onClick={() => scrollProducts('left')}
                className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full bg-[#2D5F3F] text-white flex items-center justify-center hover:bg-[#D4AF37] hover:scale-110 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 cursor-pointer"
                disabled={currentProductSlide === 0}
              >
                <i className="ri-arrow-left-line text-xl sm:text-2xl"></i>
              </button>
              <button
                onClick={() => scrollProducts('right')}
                className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full bg-[#2D5F3F] text-white flex items-center justify-center hover:bg-[#D4AF37] hover:scale-110 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 cursor-pointer"
                disabled={currentProductSlide >= (window.innerWidth < 768 ? featuredProducts.length - 1 : featuredProducts.length - 3)}
              >
                <i className="ri-arrow-right-line text-xl sm:text-2xl"></i>
              </button>
            </div>
          </div>

          <div className="relative overflow-hidden" data-product-shop>
            <div
              className="flex gap-4 sm:gap-6 transition-transform duration-500 ease-out"
              style={{ 
                transform: window.innerWidth < 768 
                  ? `translateX(-${currentProductSlide * 100}%)` 
                  : `translateX(-${currentProductSlide * (100 / 3)}%)`
              }}
            >
              {featuredProducts.map((product) => (
                <div
                  key={product.id}
                  onMouseEnter={() => setHoveredProduct(product.id)}
                  onMouseLeave={() => setHoveredProduct(null)}
                  className="min-w-full md:min-w-[calc(33.333%-16px)] bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-400 overflow-hidden group cursor-pointer hover:-translate-y-3"
                >
                  <div className="relative w-full h-72 sm:h-80 md:h-96 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-600"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <button className="bg-white text-[#2D5F3F] px-6 py-3 rounded-full font-semibold hover:bg-[#D4AF37] hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer translate-y-4 group-hover:translate-y-0 transition-transform">
                        Quick View
                      </button>
                    </div>
                  </div>
                  <div className="p-4 sm:p-6 relative">
                    <span className="inline-block px-3 sm:px-4 py-1 bg-[#2D5F3F]/10 text-[#2D5F3F] text-xs font-semibold rounded-full mb-2 sm:mb-3">
                      {product.category}
                    </span>
                    <h3 className="text-lg sm:text-xl font-semibold text-[#2C2C2C] mb-2 sm:mb-3 line-clamp-2 group-hover:text-[#2D5F3F] transition-colors duration-300">
                      {product.name}
                    </h3>
                    <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                      <span className="text-xl sm:text-2xl font-bold text-[#2D5F3F]">${product.price}</span>
                      <span className="text-sm sm:text-base text-gray-400 line-through">${product.originalPrice}</span>
                    </div>
                    <button className="absolute bottom-4 sm:bottom-6 right-4 sm:right-6 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#2D5F3F] text-white flex items-center justify-center hover:bg-[#D4AF37] hover:scale-110 hover:rotate-12 transition-all duration-300 cursor-pointer">
                      <i className="ri-shopping-cart-line text-lg sm:text-xl"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-center gap-2 mt-6 sm:mt-8">
            {Array.from({ length: window.innerWidth < 768 ? featuredProducts.length : featuredProducts.length - 2 }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentProductSlide(index)}
                className={`h-2 sm:h-3 rounded-full transition-all duration-300 cursor-pointer ${
                  currentProductSlide === index ? 'bg-[#D4AF37] w-6 sm:w-8' : 'bg-gray-300 w-2 sm:w-3 hover:bg-[#2D5F3F]'
                }`}
              ></button>
            ))}
          </div>
        </div>
      </section>

      {/* Educational Content Grid */}
      <section
        ref={eduAnim.ref as React.RefObject<HTMLElement>}
        className="py-16 sm:py-24 px-4 bg-[#F8FAF9]"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                icon: 'ri-book-open-line',
                color: '#2D5F3F',
                border: 'border-[#2D5F3F]',
                title: 'Growing Guides',
                desc: 'Comprehensive cultivation tutorials from seed to harvest. Learn organic methods, optimal conditions, and expert techniques for thriving plants.',
                link: '/guides',
                linkLabel: 'Explore Guides',
                isLink: true
              },
              {
                icon: 'ri-bug-line',
                color: '#D4AF37',
                border: 'border-[#D4AF37]',
                title: 'Pest Management',
                desc: 'Natural solutions for protecting your crops. Identify common pests and diseases, implement organic prevention strategies for healthy gardens.',
                link: '/pest-management',
                linkLabel: 'Learn More',
                isLink: true
              },
              {
                icon: 'ri-group-line',
                title: 'Community Stories',
                desc: 'Join thousands of growers worldwide sharing experiences, tips, and successes. Connect with passionate cultivators from every continent.',
                color: '#8B4513',
                link: '/community',
                linkLabel: 'Read Stories',
                isLink: true
              }
            ].map((card, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl p-8 sm:p-12 md:p-16 border-l-4 ${card.border} hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 group`}
                style={{
                  opacity: eduAnim.visibleItems[index] ? 1 : 0,
                  transform: eduAnim.visibleItems[index] ? 'translateY(0)' : 'translateY(40px)',
                  transition: 'opacity 0.6s ease-out, transform 0.6s ease-out, box-shadow 0.3s, translate 0.3s'
                }}
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 flex items-center justify-center mb-4 sm:mb-6">
                  <i
                    className={`${card.icon} text-4xl sm:text-5xl transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6`}
                    style={{ color: card.color }}
                  ></i>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-[#2C2C2C] mb-3 sm:mb-4">{card.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4 sm:mb-6 line-clamp-3">{card.desc}</p>
                {card.isLink ? (
                  <Link
                    to={card.link}
                    className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold uppercase tracking-wide hover:gap-4 transition-all duration-300 cursor-pointer"
                    style={{ color: card.color }}
                  >
                    {card.linkLabel}
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                ) : (
                  <a
                    href={card.link}
                    className="inline-flex items-center gap-2 text-xs sm:text-sm font-semibold uppercase tracking-wide hover:gap-4 transition-all duration-300 cursor-pointer"
                    style={{ color: card.color }}
                  >
                    {card.linkLabel}
                    <i className="ri-arrow-right-line"></i>
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Preview Section */}
      <section
        ref={blogAnim.ref as React.RefObject<HTMLElement>}
        className="py-16 sm:py-24 md:py-32 px-4"
        style={{
          opacity: blogAnim.isVisible ? 1 : 0,
          transform: blogAnim.isVisible ? 'translateY(0)' : 'translateY(50px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-6 sm:gap-8">
            <div className="w-full md:w-[55%]">
              <Link to="/blog" className="block group cursor-pointer">
                <div className="relative rounded-3xl overflow-hidden h-80 sm:h-96 md:h-[500px]">
                  <img
                    src={blogPosts[0].image}
                    alt={blogPosts[0].title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-black/90 transition-all duration-500"></div>
                  <span className="absolute top-4 sm:top-6 left-4 sm:left-6 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/90 text-[#2D5F3F] text-xs font-bold rounded-full">
                    {blogPosts[0].category}
                  </span>
                  <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8 translate-y-2 group-hover:translate-y-0 transition-transform duration-400">
                    <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4 leading-tight group-hover:text-[#D4AF37] transition-colors duration-300">
                      {blogPosts[0].title}
                    </h3>
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#D4AF37] flex items-center justify-center text-white font-bold text-sm sm:text-base">
                        {blogPosts[0].author.charAt(0)}
                      </div>
                      <div className="text-white/90 text-xs sm:text-sm">
                        <span className="font-semibold">{blogPosts[0].author}</span>
                        <span className="mx-2">•</span>
                        <span>{blogPosts[0].date}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>

            <div className="w-full md:w-[45%] flex flex-col gap-4 sm:gap-6">
              {blogPosts.slice(1).map((post, i) => (
                <Link
                  key={post.id}
                  to="/blog"
                  className="flex gap-3 sm:gap-4 bg-white rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer hover:-translate-x-1"
                  style={{
                    transitionDelay: `${i * 100}ms`
                  }}
                >
                  <div className="w-24 h-24 sm:w-32 sm:h-32 flex-shrink-0 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 py-3 sm:py-4 pr-3 sm:pr-4">
                    <span className="inline-block px-2 sm:px-3 py-0.5 sm:py-1 bg-[#2D5F3F]/10 text-[#2D5F3F] text-xs font-semibold rounded-full mb-1.5 sm:mb-2">
                      {post.category}
                    </span>
                    <h4 className="text-base sm:text-lg font-bold text-[#2C2C2C] mb-1.5 sm:mb-2 line-clamp-2 group-hover:text-[#2D5F3F] transition-colors duration-300">
                      {post.title}
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-500">{post.readTime}</p>
                  </div>
                </Link>
              ))}

              <Link
                to="/blog"
                className="mt-2 sm:mt-4 text-center py-3 sm:py-4 border-2 border-[#2D5F3F] text-[#2D5F3F] rounded-full font-semibold hover:bg-[#2D5F3F] hover:text-white hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm sm:text-base"
              >
                View All Articles
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Affiliate Banner */}
      <section className="py-10 px-4 bg-[#F8FAF9]">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-3">
          <p className="text-xs text-gray-400 uppercase tracking-widest font-semibold">Our Partners</p>
          <a
            href="https://www.cannatop100.com?a_aid=ElixirSue&a_bid=ebcdd1a5"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block hover:opacity-80 transition-opacity duration-300 cursor-pointer"
          >
            <img
              src="https://www.greenaffiliates.com/accounts/default1/nxkkkeyb/ebcdd1a5.png"
              alt="CannaTOP 100 - Top Cannabis Sites"
              title="CannaTOP 100"
              width={728}
              height={90}
              className="mx-auto rounded-lg shadow-md max-w-full"
            />
          </a>
          <img
            alt=""
            src="https://www.greenaffiliates.com/scripts/nxkkkeyi?a_aid=ElixirSue&a_bid=ebcdd1a5"
            width={1}
            height={1}
          />
        </div>
      </section>

      {/* Newsletter Signup Block */}
      <section
        ref={newsletterAnim.ref as React.RefObject<HTMLElement>}
        className="py-16 sm:py-24 md:py-32 px-4 relative overflow-hidden"
        style={{
          opacity: newsletterAnim.isVisible ? 1 : 0,
          transform: newsletterAnim.isVisible ? 'translateY(0)' : 'translateY(40px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A3A2A] to-[#2D5F3F]"></div>
        <div className="absolute inset-0 opacity-10">
          <i className="ri-leaf-line text-[200px] sm:text-[300px] md:text-[400px] text-white absolute top-1/2 left-1/4 transform -translate-x-1/2 -translate-y-1/2 rotate-12"></i>
          <i className="ri-leaf-line text-[150px] sm:text-[200px] md:text-[300px] text-white absolute top-1/3 right-1/4 transform translate-x-1/2 -rotate-12"></i>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 px-4">
            Join Our Global Wellness Community
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-[#F5F1E8] mb-8 sm:mb-12 px-4">
            Get expert tips, exclusive offers, and growing guides delivered worldwide
          </p>

          <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-3 sm:gap-0 max-w-2xl mx-auto mb-4 sm:mb-6 px-4">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              required
              className="flex-1 h-14 sm:h-16 px-6 sm:px-8 rounded-full sm:rounded-l-full sm:rounded-r-none text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[#D4AF37] transition-shadow duration-300"
            />
            <button
              type="submit"
              className="h-14 sm:h-16 px-10 sm:px-12 bg-[#D4AF37] text-[#1A3A2A] font-bold rounded-full sm:rounded-l-none sm:rounded-r-full hover:bg-[#F5F1E8] hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer text-sm sm:text-base"
            >
              SUBSCRIBE
            </button>
          </form>

          <p className="text-xs sm:text-sm text-white/70 px-4">
            We respect your privacy. Unsubscribe anytime.
          </p>
        </div>
      </section>

      {/* Community CTA Section */}
      <section
        id="community"
        ref={communityAnim.ref as React.RefObject<HTMLElement>}
        className="relative py-16 sm:py-24 md:py-32 px-4 overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=diverse%20group%20of%20people%20from%20different%20cultures%20and%20ethnicities%20sharing%20knowledge%20about%20cannabis%20cultivation%20in%20bright%20modern%20greenhouse%20collaborative%20learning%20environment%20natural%20sunlight%20happy%20engaged%20faces%20global%20community%20sustainable%20farming%20education%20professional%20photography&width=1920&height=800&seq=community001&orientation=landscape"
            alt="Global Community"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>

        <div
          className="relative z-10 max-w-3xl mx-auto text-center"
          style={{
            opacity: communityAnim.isVisible ? 1 : 0,
            transform: communityAnim.isVisible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.97)',
            transition: 'opacity 0.9s ease-out, transform 0.9s ease-out'
          }}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 px-4">
            Join Thousands Worldwide
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-[#F5F1E8] mb-8 sm:mb-12 leading-relaxed px-4">
            Connect with passionate growers from every continent. Share your experiences, learn from experts, and grow together in our supportive global community.
          </p>
          <button className="group bg-[#D4AF37] text-[#1A3A2A] px-10 sm:px-12 py-4 sm:py-5 rounded-full text-base sm:text-lg font-bold hover:bg-[#F5F1E8] hover:scale-105 transition-all duration-300 flex items-center gap-3 mx-auto whitespace-nowrap cursor-pointer shadow-xl">
            JOIN GLOBAL COMMUNITY
            <i className="ri-arrow-right-line text-xl sm:text-2xl group-hover:translate-x-2 transition-transform duration-300"></i>
          </button>

          <div className="flex justify-center items-center gap-1 sm:gap-2 mt-6 sm:mt-8 px-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#2D5F3F] border-2 border-white flex items-center justify-center text-white font-bold -ml-2 sm:-ml-3 first:ml-0 hover:scale-110 hover:z-10 transition-transform duration-300 cursor-pointer"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <i className="ri-user-line text-xl sm:text-2xl"></i>
              </div>
            ))}
            <span className="ml-3 sm:ml-4 text-sm sm:text-base text-white font-semibold">+50,000 members</span>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes kenburns {
          from { transform: scale(1.05) translateX(0); }
          to { transform: scale(1.12) translateX(-1%); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* ── Smoke Stream Animations ── */
        @keyframes smokeRise {
          0%   { transform: translateY(0) translateX(0) scaleX(1);   opacity: 0; }
          10%  { opacity: 0.18; }
          50%  { transform: translateY(-45vh) translateX(18px) scaleX(1.6); opacity: 0.12; }
          100% { transform: translateY(-100vh) translateX(-10px) scaleX(2.2); opacity: 0; }
        }
        @keyframes smokeRise2 {
          0%   { transform: translateY(0) translateX(0) scaleX(1);   opacity: 0; }
          10%  { opacity: 0.14; }
          50%  { transform: translateY(-50vh) translateX(-22px) scaleX(1.8); opacity: 0.10; }
          100% { transform: translateY(-100vh) translateX(15px) scaleX(2.5); opacity: 0; }
        }
        @keyframes smokeRise3 {
          0%   { transform: translateY(0) translateX(0) scaleX(1);   opacity: 0; }
          15%  { opacity: 0.20; }
          55%  { transform: translateY(-40vh) translateX(30px) scaleX(1.4); opacity: 0.13; }
          100% { transform: translateY(-100vh) translateX(-20px) scaleX(2.0); opacity: 0; }
        }
        @keyframes smokePuff {
          0%   { transform: translate(0, 0) scale(0.6); opacity: 0; }
          20%  { opacity: 0.15; }
          60%  { transform: translate(var(--dx), -38vh) scale(2.2); opacity: 0.08; }
          100% { transform: translate(var(--dx2), -80vh) scale(3.5); opacity: 0; }
        }

        .smoke-stream {
          position: absolute;
          bottom: 0;
          width: 60px;
          height: 100%;
          background: linear-gradient(
            to top,
            rgba(180, 230, 180, 0.22) 0%,
            rgba(140, 200, 140, 0.14) 30%,
            rgba(100, 180, 120, 0.07) 60%,
            transparent 100%
          );
          filter: blur(22px);
          border-radius: 50%;
          transform-origin: bottom center;
        }
        .smoke-stream-1 {
          left: 12%;
          animation: smokeRise 9s ease-in-out infinite;
          animation-delay: 0s;
          width: 50px;
          filter: blur(28px);
        }
        .smoke-stream-2 {
          left: 28%;
          animation: smokeRise2 11s ease-in-out infinite;
          animation-delay: 2.5s;
          width: 70px;
          filter: blur(20px);
        }
        .smoke-stream-3 {
          left: 48%;
          animation: smokeRise3 13s ease-in-out infinite;
          animation-delay: 1s;
          width: 80px;
          filter: blur(18px);
          background: linear-gradient(
            to top,
            rgba(200, 240, 200, 0.25) 0%,
            rgba(160, 210, 160, 0.15) 35%,
            rgba(120, 190, 130, 0.07) 65%,
            transparent 100%
          );
        }
        .smoke-stream-4 {
          left: 65%;
          animation: smokeRise 10s ease-in-out infinite;
          animation-delay: 4s;
          width: 65px;
          filter: blur(24px);
        }
        .smoke-stream-5 {
          left: 82%;
          animation: smokeRise2 12s ease-in-out infinite;
          animation-delay: 6s;
          width: 55px;
          filter: blur(26px);
        }

        .smoke-puff {
          position: absolute;
          bottom: 5%;
          border-radius: 50%;
          background: radial-gradient(
            circle,
            rgba(180, 230, 180, 0.18) 0%,
            rgba(140, 200, 140, 0.10) 50%,
            transparent 100%
          );
          filter: blur(30px);
          animation: smokePuff 14s ease-in-out infinite;
        }
        .smoke-puff-1 { left: 8%;  width: 120px; height: 120px; --dx: 30px;  --dx2: -20px; animation-delay: 0s;   animation-duration: 12s; }
        .smoke-puff-2 { left: 22%; width: 90px;  height: 90px;  --dx: -25px; --dx2: 40px;  animation-delay: 3s;   animation-duration: 15s; }
        .smoke-puff-3 { left: 42%; width: 150px; height: 150px; --dx: 20px;  --dx2: -35px; animation-delay: 1.5s; animation-duration: 13s; }
        .smoke-puff-4 { left: 60%; width: 100px; height: 100px; --dx: -30px; --dx2: 25px;  animation-delay: 5s;   animation-duration: 11s; }
        .smoke-puff-5 { left: 75%; width: 130px; height: 130px; --dx: 35px;  --dx2: -15px; animation-delay: 7s;   animation-duration: 16s; }
        .smoke-puff-6 { left: 90%; width: 80px;  height: 80px;  --dx: -20px; --dx2: 30px;  animation-delay: 2s;   animation-duration: 10s; }
      `}</style>
    </div>
  );
}